import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './NearbySearch.scss';
import { Fragment, useContext, useEffect, useState } from 'react';
import ThemeContext from '../../context/themeProvider';
import request from '../../request';
import store from '../../store/store';
import appSlice from '../../store/appSlice';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { faLocationArrow, faBagShopping, faBasketShopping, faBed, faBellConcierge, faBook, faBookAtlas, faBookOpen, faBreadSlice, faBuildingColumns, faChurch, faCircleNotch, faColumns, faDiamond, faDiamondTurnRight, faDumbbell, faGasPump, faGem, faKitMedical, faLandmark, faLandmarkAlt, faMartiniGlass, faMonument, faMugSaucer, faMuseum, faPalette, faPallet, faPersonDress, faSchool, faSearch, faShoePrints, faStore, faSuitcaseRolling, faTheaterMasks, faUniversity, faUserTie, faUtensils } from '@fortawesome/free-solid-svg-icons';
import Select from '../select/Select';
import translations from '../../translations';

function NearbySearch(props) {
  const [message, setMessage] = useState('Μοιραστείτε τη τοποθεσία σας μαζί μας');
  const [selectedCategory, setSelectedCategory] = useState(-1);
  const [geolocationSupported, setGeoLocationSupported] = useState(false);
  const [geolocationStatus, setGeolocationStatus] = useState(null);
  const [fetchingData, setFetchingData] = useState(false);

  const categories = useSelector((state) => state.app.categories);
  const geolocation = useSelector((state) => state.app.geolocation);
  
  const languageCode = useSelector((state) => state.app.geolocation.languageCode);
  const t = translations[languageCode];

  const { theme } = useContext(ThemeContext);

  const navigate = useNavigate();

  useEffect(() => {
    setGeoLocationSupported(navigator.geolocation);
  }, []);

  useEffect(() => {
    if (geolocationSupported) {
      if (!navigator.permissions?.query) {
        return;
      }

      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        setGeolocationStatus(result.state);

        if (result.state === 'granted') {
          setMessage('Βρείτε καταστήματα κοντά σας');
        }
      }).catch(() => setGeolocationStatus('error'));
    }
  }, [geolocationSupported]);

  useEffect(() => {
    switch (geolocationStatus) {
      case 'denied':
        setMessage('Ενεργοποιήστε το διαμοιρασμό τοποθεσίας')
        break;
      case 'position-unavailable':
      case 'timeout':
      case 'error':
        setMessage('Δεν καταφέραμε να προσδιορίσουμε τη τοποθεσία σας')
        break;
    }
  }, [geolocationStatus]);

  function onGrantNavigatorPermissionsError(error) {
    switch(error.code) {
      case error.PERMISSION_DENIED:
        setGeolocationStatus('denied');
        break;
      case error.POSITION_UNAVAILABLE:
        setGeolocationStatus('position-unavailable');
        break;
      case error.TIMEOUT:
        setGeolocationStatus('timeout');
        break;
      case error.UNKNOWN_ERROR:
        setGeolocationStatus('error');
        break;
    }
  }

  function grantNavigatorPermissions() {
    navigator.geolocation.getCurrentPosition(search, onGrantNavigatorPermissionsError, {
      enableHighAccuracy: true,
    });
  }

  function search(position) {
    if (fetchingData) {
      return false;
    }
    
    setFetchingData(true);
    setMessage('Αναζήτηση...');

    store.dispatch(appSlice.actions.updateGeolocation({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    }));
    
    const query = new URLSearchParams({ languageCode: geolocation.languageCode });

    if (selectedCategory > 0) {
      query.set('category', selectedCategory);
    }

    request.post(`nearby-search?${query.toString()}`, {
      lat: position.coords.latitude,
      long: position.coords.longitude,
    }).then((response) => {
      store.dispatch(appSlice.actions.updateSearchResults(response.data));
      setFetchingData(false);
      const categoryQuery = selectedCategory > 0 ? `?category=${selectedCategory}` : '';
      navigate(`/results/nearby/${position.coords.latitude},${position.coords.longitude}${categoryQuery}`);
    }).catch(() => {
      setFetchingData(false);
      setMessage('Δεν ήταν δυνατή η αναζήτηση. Δοκιμάστε ξανά.');
    });
  }

  function onCategorySelect(category) {
    setSelectedCategory(category.id > 0 ? category.id : -1);
  }

  const categoryIcons = [
    {
      slug: 'restaurants',
      icon: <FontAwesomeIcon icon={faBellConcierge} size="lg" />,
    },
    {
      slug: 'supermarkets',
      icon: <FontAwesomeIcon icon={faBasketShopping} size="lg" />,
    },
    {
      slug: 'bakeries',
      icon: <FontAwesomeIcon icon={faBreadSlice} size="lg" />,
    },
    {
      slug: 'cafes',
      icon: <FontAwesomeIcon icon={faMugSaucer} size="lg" />,
    },
    {
      slug: 'greek-orthodox-churches',
      icon: <FontAwesomeIcon icon={faChurch} size="lg" />,
    },
    {
      slug: 'museums',
      icon: <FontAwesomeIcon icon={faBookOpen} size="lg" />,
    },
    {
      slug: 'greek-cultural-centers',
      icon: <FontAwesomeIcon icon={faTheaterMasks} size="lg" />,
    },
    {
      slug: 'greek-schools',
      icon: <FontAwesomeIcon icon={faSchool} size="lg" />,
    },
    {
      slug: 'travel-agencies',
      icon: <FontAwesomeIcon icon={faSuitcaseRolling} size="lg" />,
    },
    {
      slug: 'jewelry-stores',
      icon: <FontAwesomeIcon icon={faGem} size="lg" />,
    },
    {
      slug: 'clothing-stores',
      icon: <FontAwesomeIcon icon={faBagShopping} size="lg" />,
    },
    {
      slug: 'markets-outdoor-markets',
      icon: <FontAwesomeIcon icon={faStore} size="lg" />,
    },
    {
      slug: 'sightseeing-spots',
      icon: <FontAwesomeIcon icon={faBuildingColumns} size="lg" />,
    },
    {
      slug: 'nightlife-bars-clubs',
      icon: <FontAwesomeIcon icon={faMartiniGlass} size="lg" />,
    },
    {
      slug: 'historical-monuments',
      icon: <FontAwesomeIcon icon={faMonument} size="lg" />,
    },
  ];

  return (
    <div className={`NearbySearch ${theme}`}>
      <Select
        placeholder={t['nearby-category-placeholder']}
        data={[
          { id: 0, name: t['all-nearby-places'], slug: 'all' },
          ...categories,
        ]}
        icons={categoryIcons}
        onChange={onCategorySelect}
      ></Select>
      {
        geolocationSupported && <Fragment>
          <span className="tip">{message}</span>

          <div className="btn-get-location-container">
            <div
              className="btn-get-location"
              onClick={grantNavigatorPermissions}
            >
              <FontAwesomeIcon icon={faLocationArrow} size="3x" />
            </div>
          </div>
        </Fragment>
      }
    </div>
  );
}

export default NearbySearch;
